import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Table,
  Tag,
  Button,
  Space,
  message,
  Switch,
  Typography,
  Divider,
  Alert,
} from 'antd';
import { useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const { TextArea } = Input;
const { Text } = Typography;

const RefundModal = ({ visible, order, onClose }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [returnQuantities, setReturnQuantities] = useState({});

  useEffect(() => {
    if (visible && order) {
      const initial = {};
      order.items?.forEach((item) => {
        initial[item.id] = 0;
      });
      setReturnQuantities(initial);
      form.resetFields();
      form.setFieldsValue({
        refund_type: 'partial',
        auto_restock: true,
      });
    }
  }, [visible, order, form]);

  const getMaxRefundAmount = () => {
    if (!order?.items) return 0;
    return order.items.reduce((sum, item) => {
      const qty = returnQuantities[item.id] || 0;
      return sum + parseFloat(item.price_at_order) * qty;
    }, 0);
  };

  const handleQuantityChange = (itemId, value) => {
    setReturnQuantities((prev) => ({ ...prev, [itemId]: value }));
  };

  const processRefundMutation = useMutation(
    async (values) => {
      const selectedItems = order.items
        .filter((item) => (returnQuantities[item.id] || 0) > 0)
        .map((item) => ({
          order_item_id: item.id,
          return_quantity: returnQuantities[item.id],
        }));

      if (selectedItems.length === 0) {
        throw new Error('Please select at least one item to refund');
      }

      const refundAmount = getMaxRefundAmount();

      const payload = {
        refund_type: values.refund_type,
        refund_amount: refundAmount,
        reason: values.reason,
        admin_notes: values.admin_notes || null,
        auto_restock: values.auto_restock,
        returned_items: selectedItems,
      };

      const response = await api.post(`/admin/orders/${order.id}/refunds`, payload);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Refund processed successfully');
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries('order-stats');
        queryClient.invalidateQueries('billing-data');
        queryClient.invalidateQueries(['refunds']);
        onClose();
      },
      onError: (error) => {
        const msg = error.response?.data?.message || error.message || 'Failed to process refund';
        message.error(msg);
      },
    },
  );

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      processRefundMutation.mutate(values);
    });
  };

  const itemColumns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <Space>
          <Text strong>{record.product?.name || record.item_name || 'Unknown Item'}</Text>
          {record.product?.type === 'service' && <Tag color="blue">SERVICE</Tag>}
        </Space>
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'price_at_order',
      key: 'price',
      width: 120,
      render: (price) => `Rs. ${parseFloat(price || 0).toFixed(2)}`,
    },
    {
      title: 'Ordered Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
    },
    {
      title: 'Return Qty',
      key: 'return_qty',
      width: 120,
      render: (_, record) => (
        <InputNumber
          min={0}
          max={record.quantity}
          value={returnQuantities[record.id] || 0}
          onChange={(value) => handleQuantityChange(record.id, value)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: 'Refund Amount',
      key: 'refund_amount',
      width: 130,
      render: (_, record) => {
        const qty = returnQuantities[record.id] || 0;
        const amount = parseFloat(record.price_at_order || 0) * qty;
        return `Rs. ${amount.toFixed(2)}`;
      },
    },
  ];

  const hasSelectedItems = Object.values(returnQuantities).some((q) => q > 0);
  const totalRefundAmount = getMaxRefundAmount();

  return (
    <Modal
      title={`Process Refund — Order #${order?.order_number || order?.id}`}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          danger
          onClick={handleSubmit}
          loading={processRefundMutation.isLoading}
          disabled={!hasSelectedItems}
        >
          Process Refund (Rs. {totalRefundAmount.toFixed(2)})
        </Button>,
      ]}
    >
      {order && (
        <div>
          <Alert
            message={`Order Total: Rs. ${parseFloat(order.total_amount || 0).toFixed(2)} | Payment: ${order.payment_method?.toUpperCase()}`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Text strong>Select Items to Refund</Text>
          <Table
            dataSource={order.items || []}
            columns={itemColumns}
            rowKey="id"
            pagination={false}
            size="small"
            style={{ marginTop: 8, marginBottom: 16 }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>Total Refund Amount</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong style={{ color: '#cf1322' }}>
                    Rs. {totalRefundAmount.toFixed(2)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />

          <Divider />

          <Form
            form={form}
            layout="vertical"
            initialValues={{ refund_type: 'partial', auto_restock: true }}
          >
            <Form.Item name="refund_type" label="Refund Type">
              <Select>
                <Select.Option value="partial">Partial Refund</Select.Option>
                <Select.Option value="item_level">Item Level Refund</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="reason"
              label="Reason"
              rules={[{ required: true, message: 'Please provide a reason for the refund' }]}
            >
              <TextArea rows={3} placeholder="e.g., Customer requested fewer items after fitting" />
            </Form.Item>

            <Form.Item name="admin_notes" label="Admin Notes (internal)">
              <TextArea rows={2} placeholder="Optional internal notes" />
            </Form.Item>

            <Form.Item name="auto_restock" label="Auto Restock" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Form>
        </div>
      )}
    </Modal>
  );
};

export default RefundModal;
